import React from 'react';
import { motion } from 'framer-motion';
import { FaLightbulb, FaHeart, FaRocket, FaUsers } from 'react-icons/fa';
import PublicNav from '../../components/PublicNav/PublicNav';
import PublicFooter from '../../components/PublicFooter/PublicFooter';
import './About.css';

const About = () => {
  const values = [
    {
      icon: <FaLightbulb />,
      title: 'Innovation',
      description: 'We constantly innovate to provide cutting-edge solutions for project management'
    },
    {
      icon: <FaHeart />,
      title: 'Customer First',
      description: 'Our customers are at the heart of everything we do'
    },
    {
      icon: <FaRocket />,
      title: 'Excellence',
      description: 'We strive for excellence in every aspect of our platform'
    },
    {
      icon: <FaUsers />,
      title: 'Collaboration',
      description: 'We believe in the power of teamwork and collaboration'
    }
  ];

  const team = [
    {
      name: 'Sam Sebastian',
      role: 'Business Head',
      description:'Visionary leader with 30+ years in enterprise software',
      image: 'https://img.icons8.com/fluency/512/user-male-circle.png'
    },
    {
      name: 'Shara',
      role: 'MD',
      description:'Tech innovator specializing in scalable solutions',
      image: 'https://img.icons8.com/fluency/512/user-male-circle.png'
    },
    {
      name: 'Joshna',
      role: 'Scrum of Scrums',
      description:'Product strategist focused on Business',
      image: 'https://img.icons8.com/fluency/512/user-male-circle.png'
    },
    {
      name: 'John Paul',
      role: 'BDM',
      description:'Sales excellence and team leadership expert',
      image: 'https://img.icons8.com/fluency/512/user-male-circle.png'
    },
    {
      name: 'Nagendra C',
      role: 'Pre-Sales Consultant',
      description:'Expert collaborating global clientele with Digital Technologies',
      image: 'https://img.icons8.com/fluency/512/user-male-circle.png'
    },
    {
      name: 'Prem',
      role: 'AI Consultant',
      description:'Specialist in scaling business solutions',
      image: 'https://img.icons8.com/fluency/512/user-male-circle.png'
    }
  ];

  const milestones = [
    { year: '2009', event: 'Spearheaded Consultation' , description: 'Started with a vision to transform ITSM'  },
    { year: '2013', event: 'Company Founded', description:'Reached our first major milestone' },
    { year: '2017', event: 'Global Partnership', description:'Secured global operations' },
    { year: '2023', event: 'Multi Product Services', description:'Portfolio of products and on-demand services' },
    { year: '2026', event: 'AI Driven Solutions' , description:'Leveraging with advanced AI models for Future-Proof Technology' }
  ];

  return (
    <div className="about-page">
      <PublicNav />
      {/* Hero Section */}
      <section className="about-hero">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="about-hero-content"
          >
            <h1 className="about-title">About Us</h1>
            <p className="about-subtitle">
              We're on a mission to revolutionize project management and help teams 
              achieve their goals more efficiently.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story Section */}
      <section className="story-section">
        <div className="container">
          <div className="story-grid">
            <motion.div
              className="story-content"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="section-title">Our Story</h2>
              <p className="story-text">
                Founded in 2009, our platform was born from a simple idea: project management 
                should be intuitive, powerful, and accessible to everyone. We started as a small 
                team of passionate developers and designers who were frustrated with the complexity 
                of existing tools.
              </p>
              <p className="story-text">
                Today, we serve thousands of teams worldwide, from startups to enterprises, 
                helping them manage their projects more effectively. Our commitment to innovation 
                and customer satisfaction drives everything we do.
              </p>
            </motion.div>
            <motion.div
              className="story-image"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div>
              {/* <div className="image-placeholder"> */}
                {/* <FaRocket className="placeholder-icon" /> */}
               <img src="./Group.jpg" alt="Group" style={{borderRadius:'20px'}}/>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="values-section">
        <div className="container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">Our Values</h2>
            <p className="section-subtitle">
              The principles that guide our work and decisions
            </p>
          </motion.div>

          <div className="values-grid">
            {values.map((value, index) => (
              <motion.div
                key={index}
                className="value-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="value-icon">{value.icon}</div>
                <h3 className="value-title">{value.title}</h3>
                <p className="value-description">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="timeline-section">
        <div className="container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">Our Journey</h2>
            <p className="section-subtitle">Key milestones in our growth</p>
          </motion.div>

          <div className="timeline">
            {milestones.map((milestone, index) => (
              <motion.div
                key={index}
                className="timeline-item"
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="timeline-content">
                  <div className="timeline-year">{milestone.year}</div>
                  <h1 className="font-bold">{milestone.event}</h1>  
                  <div>{milestone.description}</div>  
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="team-section">
        <div className="container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">Meet Our Team</h2>
            <p className="section-subtitle">
              The talented people behind our success
            </p>
          </motion.div>

          <div className="team-grid">
            {team.map((member, index) => (
              <motion.div
                key={index}
                className="team-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
              >
                <div className="team-image">
                  <img src={member.image} alt={member.name} />
                </div>
                <h3 className="team-name">{member.name}</h3>
                <p className="team-role">{member.role}</p>
                <span>{member.description}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      <PublicFooter />
    </div>
  );
};

export default About;


